import React, { useState, useEffect, useCallback } from 'react';
import { bankAccountAPI, formatCurrency } from '../services/api';
import { toast } from 'sonner';
import { Plus, Building2, ArrowRightLeft, Edit, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import api from '../services/api';

export default function BankAccountsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ bank_name: '', account_number: '', ifsc_code: '', branch: '', account_type: 'current', opening_balance: 0 });
  const [transfer, setTransfer] = useState({ from: '', to: '', amount: 0, narration: '' });
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => localStorage.getItem('bank_auto_sync') === 'true');

  const load = useCallback(() => {
    setLoading(true);
    bankAccountAPI.list().then(r => setAccounts(r.data || []))
      .catch(() => toast.error('Failed to load bank accounts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-sync on load if enabled
  useEffect(() => {
    if (autoSyncEnabled) {
      bankAccountAPI.syncFromCOA().then(r => {
        if (r.data?.count > 0) {
          toast.success(`Auto-synced ${r.data.count} bank accounts from system`);
          load();
        }
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await bankAccountAPI.syncFromCOA();
      if (r.data?.count > 0) {
        toast.success(`Synced ${r.data.count} new bank accounts from Chart of Accounts`);
        load();
      } else {
        toast.info('All bank accounts are already synced');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const toggleAutoSync = () => {
    const newVal = !autoSyncEnabled;
    setAutoSyncEnabled(newVal);
    localStorage.setItem('bank_auto_sync', newVal ? 'true' : 'false');
    toast.success(newVal ? 'Auto-sync enabled' : 'Auto-sync disabled');
  };

  const handleSave = async () => {
    if (!form.bank_name) { toast.error('Bank name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await bankAccountAPI.update(editing, form);
        toast.success('Bank account updated');
      } else {
        await bankAccountAPI.create(form);
        toast.success('Bank account created and added to Chart of Accounts');
      }
      setShowCreate(false); setEditing(null);
      setForm({ bank_name: '', account_number: '', ifsc_code: '', branch: '', account_type: 'current', opening_balance: 0 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleTransfer = async () => {
    if (!transfer.from || !transfer.to || transfer.amount <= 0) { toast.error('Fill all fields'); return; }
    if (transfer.from === transfer.to) { toast.error('Cannot transfer to same account'); return; }
    setSaving(true);
    try {
      await bankAccountAPI.transfer(transfer.from, { to_bank_id: transfer.to, amount: transfer.amount, narration: transfer.narration || 'Inter-bank transfer' });
      toast.success('Transfer completed and journal entry posted');
      setShowTransfer(false);
      setTransfer({ from: '', to: '', amount: 0, narration: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setSaving(false); }
  };

  const openEdit = (acc) => {
    setForm({ bank_name: acc.bank_name, account_number: acc.account_number, ifsc_code: acc.ifsc_code, branch: acc.branch, account_type: acc.account_type, opening_balance: acc.opening_balance });
    setEditing(acc.bank_id);
    setShowCreate(true);
  };

  const activeAccounts = accounts.filter(a => a.is_active);

  return (
    <div data-testid="bank-accounts-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Bank Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Manage bank accounts connected to the accounting engine</p>
        </div>
        <div className="flex gap-2">
          {/* Auto-sync toggle */}
          <button onClick={toggleAutoSync} data-testid="auto-sync-toggle"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 transition text-sm">
            {autoSyncEnabled
              ? <ToggleRight className="w-5 h-5 text-emerald-600" />
              : <ToggleLeft className="w-5 h-5 text-slate-400" />}
            <span className={autoSyncEnabled ? 'text-emerald-700 font-medium' : 'text-slate-500'}>
              Auto Sync
            </span>
          </button>
          {/* Sync from COA button */}
          <Button variant="outline" onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2" data-testid="sync-banks-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing...' : 'Sync from System'}
          </Button>
          {activeAccounts.length >= 2 && (
            <Button variant="outline" onClick={() => setShowTransfer(true)}
              className="flex items-center gap-2" data-testid="transfer-btn">
              <ArrowRightLeft className="w-4 h-4" /> Transfer
            </Button>
          )}
          <Button onClick={() => { setEditing(null); setForm({ bank_name: '', account_number: '', ifsc_code: '', branch: '', account_type: 'current', opening_balance: 0 }); setShowCreate(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2" data-testid="add-bank-btn">
            <Plus className="w-4 h-4" /> Add Bank Account
          </Button>
        </div>
      </div>

      {/* Summary */}
      {activeAccounts.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Accounts</p>
            <p className="text-2xl font-bold font-mono text-slate-900 mt-1">{activeAccounts.length}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Opening Balance</p>
            <p className="text-2xl font-bold font-mono text-blue-700 mt-1">Rs.{formatCurrency(activeAccounts.reduce((s, a) => s + (a.opening_balance || 0), 0))}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase">Total Current Balance</p>
            <p className="text-2xl font-bold font-mono text-emerald-700 mt-1">Rs.{formatCurrency(activeAccounts.reduce((s, a) => s + (a.current_balance || 0), 0))}</p>
          </div>
        </div>
      )}

      {/* Accounts List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : accounts.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg flex flex-col items-center justify-center py-16">
          <Building2 className="w-14 h-14 text-slate-300 mb-4" strokeWidth={1.5} />
          <p className="text-slate-500 text-sm">No bank accounts added yet</p>
          <p className="text-slate-400 text-xs mt-1">Add a bank account to start tracking balances</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {accounts.map(acc => (
            <div key={acc.bank_id} className={`bg-white border rounded-lg p-5 transition hover:shadow-sm ${acc.is_active ? 'border-slate-200' : 'border-slate-100 opacity-60'}`}
              data-testid={`bank-card-${acc.bank_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{acc.bank_name}</h3>
                    <p className="text-xs text-slate-500">{acc.branch || 'No branch'}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(acc)} className="p-1.5 hover:bg-slate-100 rounded transition" data-testid={`edit-bank-${acc.bank_id}`}>
                    <Edit className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">A/C Number</p>
                  <p className="font-mono text-slate-700">{acc.account_number || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">IFSC</p>
                  <p className="font-mono text-slate-700">{acc.ifsc_code || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Type</p>
                  <p className="capitalize text-slate-700">{acc.account_type}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">COA Code</p>
                  <p className="font-mono text-slate-700">{acc.account_code}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <div>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Opening</p>
                  <p className="font-mono text-sm text-slate-600">Rs.{formatCurrency(acc.opening_balance)}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">Current Balance</p>
                  <p className="font-mono text-lg font-bold text-blue-700">Rs.{formatCurrency(acc.current_balance)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">Bank Name</label>
              <input type="text" value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
                className="form-input" placeholder="e.g. HDFC Bank" data-testid="bank-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Account Number</label>
                <input type="text" value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))}
                  className="form-input font-mono" placeholder="e.g. 12345678" data-testid="bank-form-number" />
              </div>
              <div>
                <label className="form-label">IFSC Code</label>
                <input type="text" value={form.ifsc_code} onChange={e => setForm(f => ({ ...f, ifsc_code: e.target.value.toUpperCase() }))}
                  className="form-input font-mono" placeholder="e.g. HDFC0001234" data-testid="bank-form-ifsc" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="form-label">Branch</label>
                <input type="text" value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
                  className="form-input" placeholder="e.g. Connaught Place" data-testid="bank-form-branch" />
              </div>
              <div>
                <label className="form-label">Account Type</label>
                <select value={form.account_type} onChange={e => setForm(f => ({ ...f, account_type: e.target.value }))}
                  className="form-input" data-testid="bank-form-type">
                  <option value="current">Current</option>
                  <option value="savings">Savings</option>
                  <option value="overdraft">Overdraft</option>
                </select>
              </div>
            </div>
            {!editing && (
              <div>
                <label className="form-label">Opening Balance (Rs.)</label>
                <input type="number" value={form.opening_balance} onChange={e => setForm(f => ({ ...f, opening_balance: parseFloat(e.target.value) || 0 }))}
                  className="form-input font-mono" placeholder="0" min="0" step="0.01" data-testid="bank-form-balance" />
                <p className="text-[10px] text-slate-400 mt-1">A journal entry will be auto-posted for the opening balance</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bank-form-save">
              {saving ? 'Saving...' : editing ? 'Update' : 'Add Bank Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransfer} onOpenChange={(o) => !o && setShowTransfer(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Transfer Between Banks</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="form-label">From Account</label>
              <select value={transfer.from} onChange={e => setTransfer(t => ({ ...t, from: e.target.value }))}
                className="form-input" data-testid="transfer-from">
                <option value="">Select source</option>
                {activeAccounts.map(a => <option key={a.bank_id} value={a.bank_id}>{a.bank_name} — Rs.{formatCurrency(a.current_balance)}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">To Account</label>
              <select value={transfer.to} onChange={e => setTransfer(t => ({ ...t, to: e.target.value }))}
                className="form-input" data-testid="transfer-to">
                <option value="">Select destination</option>
                {activeAccounts.filter(a => a.bank_id !== transfer.from).map(a => <option key={a.bank_id} value={a.bank_id}>{a.bank_name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Amount (Rs.)</label>
              <input type="number" value={transfer.amount} onChange={e => setTransfer(t => ({ ...t, amount: parseFloat(e.target.value) || 0 }))}
                className="form-input font-mono" placeholder="0" min="0" step="0.01" data-testid="transfer-amount" />
            </div>
            <div>
              <label className="form-label">Narration</label>
              <input type="text" value={transfer.narration} onChange={e => setTransfer(t => ({ ...t, narration: e.target.value }))}
                className="form-input" placeholder="Optional note" data-testid="transfer-narration" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransfer(false)}>Cancel</Button>
            <Button onClick={handleTransfer} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="transfer-submit">
              {saving ? 'Transferring...' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
